import React, { useEffect, useState } from "react";
import axios from "axios";
import Grid from "../components/Grid";
import { FaPlus } from "react-icons/fa";
import { toast } from "react-toastify";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography, TextField
} from "@mui/material";
import ReimbursementModal from "../components/ReimbursementModal";


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL;

function ReimbursementPage() {

    const [data, setData] = useState([]);
    const [categories, setCategories] = useState([]);
    const [locations, setLocations] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [approveAmount, setApproveAmount] = useState("");
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsData, setDetailsData] = useState([]);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewFile, setPreviewFile] = useState("");
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10
    });
    const [totalRows, setTotalRows] = useState(0);
    const [cashbookDialog, setCashbookDialog] = useState(false);
    const [cashbooks, setCashbooks] = useState([]);
    const [selectedCashbook, setSelectedCashbook] = useState("");

    const token = sessionStorage.getItem("token");
    const user = JSON.parse(sessionStorage.getItem("user") || "{}");
    const role = user.role || "";

    const [filters, setFilters] = useState({
        search: "",
        category: "",
        location: "",
        from: "",
        to: ""
    });

    const updateFilter = (newFilters) => {
        setPaginationModel(prev => ({ ...prev, page: 0 }));
        setFilters(prev => ({ ...prev, ...newFilters }));
    };

    useEffect(() => {
        fetchReimbursements();
        fetchCategories();
        fetchLocations();
    }, []);

    useEffect(() => {
        fetchReimbursements();
    }, [paginationModel, filters]);

    useEffect(() => {

        const refreshHandler = () => {
            fetchReimbursements();
        };

        window.addEventListener(
            "pettycash-updated",
            refreshHandler
        );

        return () => {

            window.removeEventListener(
                "pettycash-updated",
                refreshHandler
            );
        };

    }, [paginationModel, filters]);

    const fetchReimbursements = async () => {
        try {
            const params = new URLSearchParams();

            params.append("transaction_type", "ReimbursementPage");
            params.append("page", paginationModel.page + 1);
            params.append("limit", paginationModel.pageSize);

            if (filters.search) params.append("search", filters.search);
            if (filters.category) params.append("category", filters.category);
            if (filters.location) params.append("location", filters.location);
            if (filters.from) params.append("startDate", filters.from);
            if (filters.to) params.append("endDate", filters.to);

            const res = await axios.get(
                `${API_BASE_URL}/pettycash/get-transactions?${params.toString()}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setData(res.data.data || []);
            setTotalRows(res.data.totalRecords || 0);

        } catch {
            toast.error("Error loading reimbursements");
        }
    };

    const fetchCategories = async()=>{

        try{

            const res = await axios.get(
                `${API_BASE_URL}/cashbook-category/get`,
                {headers:{Authorization:`Bearer ${token}`}}
            );

            const paymentCategories = res.data.data.filter(
                c => c.type === "Payment"
            );
            setCategories(paymentCategories);

        }catch(err){

            toast.error("Error loading categories");

        }

    };

    const fetchLocations = async()=>{

        try{

            const res = await axios.get(
                `${API_BASE_URL}/locations`,
                {headers:{Authorization:`Bearer ${token}`}}
            );

            setLocations(res.data.locations || []);

        }catch(err){

            console.log(err);

        }

    };

    const fetchCashbooks = async () => {

        try {

            const res = await axios.get(
                `${API_BASE_URL}/cashbook/get`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            const activeBooks =
                res.data.data.filter(
                    b => b.status === "Active"
                );

            setCashbooks(activeBooks);

        } catch (err) {

            toast.error(
                "Failed to load cashbooks"
            );
        }
    };


    const handleOpenApprove = (row) => {
        setSelectedRow(row);

        const remaining = row.amount - (row.reimbursed_amount || 0);
        setApproveAmount(remaining);

        setOpenDialog(true);
    };

    const handleApprove = async () => {
        if (approveAmount > remaining) {
            toast.error("Amount exceeds available balance");
            return;
        }

            // ==========================================
            // EXACT IOU SETTLEMENT
            // NO APPROVE AMOUNT NEEDED
            // ==========================================

        if (
            selectedRow?.transaction_type !== "IOUSettlement" &&
            approveAmount <= 0
        ) {
            toast.error("Invalid amount");
            return;
        }

                // ==========================================
                // NORMAL VALIDATION
                // ==========================================

        if (
            selectedRow?.transaction_type === "IOUSettlement" &&
            Number(selectedRow?.returned_amount || 0) !== 0 &&
            approveAmount <= 0
        ) {
            toast.error("Invalid amount");
            return;
        }
        try {

            await axios.put(
                `${API_BASE_URL}/pettycash/approve/${selectedRow._id}`,
                {
                    approve_amount: Number(approveAmount)
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            toast.success("Reimbursement approved");

            fetchReimbursements();

            window.dispatchEvent(
                new Event("pettycash-updated")
            );

        } catch (err) {
            toast.error(err.response?.data?.message || "Error");
        }

        setOpenDialog(false);
    };

    const handleReject = async (id) => {
        try {
            await axios.put(
                `${API_BASE_URL}/pettycash/reject/${id}`,
                {},
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            toast.success("Rejected");
            fetchReimbursements();

            window.dispatchEvent(
                new Event("pettycash-updated")
            );

        } catch {
            toast.error("Error");
        }
    };

    const handleIssueMoney = async (id) => {

        try {

            await axios.post(
                `${API_BASE_URL}/pettycash/issue/${id}`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            toast.success("Money issued successfully");

            fetchReimbursements();

            window.dispatchEvent(
                new Event("pettycash-updated")
            );

        } catch (err) {

            toast.error(
                err.response?.data?.message || "Error"
            );
        }
    };

    const handleConfirmReceived = async (id) => {

        try {

            await axios.post(
                `${API_BASE_URL}/pettycash/confirm-received/${id}`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            toast.success("Settlement completed");

            fetchReimbursements();

            window.dispatchEvent(
                new Event("pettycash-updated")
            );

        } catch (err) {

            toast.error(
                err.response?.data?.message || "Error"
            );
        }
    };

    const handleLinkCashbook = async () => {

        if (!selectedCashbook) {

            toast.error(
                "Select cashbook"
            );

            return;
        }

        try {

            await axios.put(
                `${API_BASE_URL}/pettycash/link-cashbook/${selectedRow._id}`,
                {
                    cash_book: selectedCashbook
                },
                {
                    headers: {
                        Authorization:
                            `Bearer ${token}`
                    }
                }
            );

            toast.success(
                "Cashbook linked"
            );

            setCashbookDialog(false);

            setSelectedCashbook("");

            fetchReimbursements()

            window.dispatchEvent(
                new Event("pettycash-updated")
            );

        } catch (err) {

            toast.error(
                err.response?.data?.message ||
                "Error"
            );
        }
    };

    const handleOpenCashbookDialog =
        async (row) => {

            setSelectedRow(row);

            await fetchCashbooks();

            setCashbookDialog(true);
        };




    const columns = [
        {
            field: "transaction_code",
            headerName: "Code",
            flex: 1,
            renderCell: (params) => {
                const user = params.row.created_by?.username || "Unknown";
                const location =
                    params.row.cash_book?.location?.location_name ||
                    params.row.location?.location_name ||
                    "Organization";

                return (
                    <div className="flex flex-col justify-center w-full h-full">
                        <span className="font-semibold text-gray-800">
                            {params.value}
                        </span>

                        <div className="flex gap-2 mt-1 text-xs">
                            <span className="bg-gray-100 px-2 py-[2px] rounded">
                                👤 {user}
                            </span>
                            <span className="bg-green-100 text-green-700 px-2 py-[2px] rounded">
                                📍 {location}
                            </span>
                        </div>
                    </div>
                );
            }
        },
        {
            field: "cashbook",
            headerName: "CashBook",
            flex: 1,
            renderCell: p => {
                const book = p.row.cash_book?.cash_book_code

                return(
                    <div className="flex justify-center items-center w-full h-full ">
                        <span> {book}</span>
                    </div>
                )
            }
        },
        {
            field: "amount",
            headerName: "Amount",
            flex: 1 ,
            renderCell: p => {

                let amount = 0;

                if (p.row.transaction_type === "IOUSettlement") {

                    amount =
                        Math.abs(
                            p.row.returned_amount || 0
                        );

                } else {

                    amount =
                        p.row.reimbursed_amount ||
                        p.row.amount ||
                        0;
                }

                return(
                    <div className="flex justify-center items-center w-full h-full ">
            <span className="text-blue-600 font-semibold">
                + Rs {amount}
            </span>
                    </div>
                )
            }
        },

        { field: "approval_status",
            headerName: "Status",
            flex: 1,
            align: "center",
            headerAlign: "center",
            renderCell: p => {
                let status = p.row.approval_status;

                if (p.row.transaction_type === "IOUSettlement") {

                    status = p.row.iou_status;

                } else if (
                    p.row.claim_payment_status === "MoneyIssued"
                ) {

                    status = "MoneyIssued";
                }

                const color =
                    status === "MoneyIssued"
                        ? "bg-blue-600"
                        : status === "Approved"
                            ? "bg-green-600"
                            : status === "Rejected"
                                ? "bg-red-600"
                                : "bg-yellow-500";

                return (
                    <div className="flex justify-center items-center w-full h-full">
                        <span className={`${color} text-white px-3 py-1 rounded-full text-xs font-medium`}>
                            {status}
                        </span>
                    </div>
                );
            }
        },
        {
            field: "details",
            headerName: "Details",
            flex: 1.3,

            renderCell: (params) => {

                const row = params.row;

                const relatedTransactions =
                    row.related_transactions || [];

                const totalClaims =
                    relatedTransactions.length;

                return (

                    <div className="flex items-center gap-2 h-full">

                        {/* NORMAL REIMBURSEMENT */}

                        {
                            row.transaction_type === "Reimbursement" && (

                                <>
                            <span
                                className="
                                    text-xs
                                    text-gray-600
                                "
                            >
                                {totalClaims} Expenses
                            </span>

                                    <span
                                        className="
                                    text-blue-600
                                    text-xs
                                    font-semibold
                                "
                                    >
                                Rs {(row.amount || 0).toLocaleString()}
                            </span>
                                </>
                            )
                        }

                        {/* IOU RELATED REIMBURSEMENT */}

                        {
                            row.transaction_type === "IOUSettlement" && (

                                <>
                            <span
                                className="
                                    text-xs
                                    text-gray-600
                                "
                            >
                                {totalClaims} Expenses
                            </span>

                                    <span
                                        className={`
                                    text-xs
                                    font-semibold
                                    ${
                                            row.returned_amount > 0
                                                ? "text-green-600"
                                                : row.returned_amount < 0
                                                    ? "text-red-600"
                                                    : "text-blue-600"
                                        }
                                `}
                                    >
                                {
                                    row.returned_amount > 0
                                        ? `+ Rs ${row.returned_amount.toLocaleString()}`
                                        : row.returned_amount < 0
                                            ? `- Rs ${Math.abs(row.returned_amount).toLocaleString()}`
                                            : "Exact Match"
                                }
                            </span>
                                </>
                            )
                        }

                        {/* VIEW BUTTON */}

                        <button
                            onClick={() => {

                                setSelectedRow(row);

                                setDetailsData(
                                    relatedTransactions
                                );

                                setDetailsOpen(true);
                            }}
                            className="
                        bg-gray-100
                        hover:bg-gray-200
                        text-gray-700
                        text-xs
                        px-3
                        py-1
                        rounded
                        transition-all
                    "
                        >
                            View
                        </button>

                    </div>
                );
            }
        },
        {
            field: "createdAt",
            headerName: "Date",
            flex: 1,
            renderCell: (params) => {
                const date = new Date(params.row.createdAt).toLocaleDateString();

                return(
                    <div className="flex justify-center items-center w-full h-full ">
                        <span> {date}</span>
                    </div>
                )
            }

        },
        ... (role === "Company Admin" || role === "Accountant"
            ? [{
                    field: "action",
                    headerName: "Action",
                    flex: 1,
                    renderCell: (p) => {

                        if (
                            role === "Company Admin" &&
                            p.row.approval_status === "Pending"
                        ) {

                            return (
                                <div className="flex items-center justify-center w-full h-full gap-2">

                                    <button
                                        onClick={() => handleOpenApprove(p.row)}
                                        className="bg-green-600 text-white px-2 py-1 rounded-full text-xs"
                                    >
                                        Approve
                                    </button>

                                    <button
                                        onClick={() => handleReject(p.row._id)}
                                        className="bg-red-600 text-white px-2 py-1 rounded-full text-xs"
                                    >
                                        Reject
                                    </button>

                                </div>
                            );
                        }

                            // ==========================================
                            // NORMAL REIMBURSEMENT
                            // ==========================================

                        if (
                            role === "Accountant" &&
                            p.row.transaction_type === "Reimbursement" &&
                            p.row.approval_status === "Approved" &&
                            !p.row.cash_book &&
                            (
                                p.row.created_by?.role === "Farm User" ||
                                p.row.created_by?.role === "Outlet User" ||
                                p.row.requested_for_user
                            )
                        ) {

                            return (

                                <div className="flex items-center justify-center w-full h-full">

                                    <button
                                        onClick={() =>
                                            handleOpenCashbookDialog(p.row)
                                        }
                                        className="
                                            bg-orange-500
                                            text-white
                                            px-3
                                            py-1
                                            rounded-full
                                            text-xs
                                        "
                                    >
                                        Link Cashbook
                                    </button>

                                </div>
                            );
                        }

                        if (
                            role === "Accountant" &&
                            p.row.transaction_type === "Reimbursement" &&
                            p.row.approval_status === "Approved" &&
                            p.row.cash_book &&
                            p.row.claim_payment_status !== "MoneyIssued"
                        ) {

                            return (

                                <div className="flex items-center justify-center w-full h-full">

                                    <button
                                        onClick={() => handleIssueMoney(p.row._id)}
                                        className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs"
                                    >
                                        Cash Issued
                                    </button>

                                </div>
                            );
                        }

                            // ==========================================
                            // IOU SETTLEMENT - REMAINING MONEY
                            // ADMIN CONFIRM RECEIVED
                            // ==========================================

                        if (
                            p.row.transaction_type === "IOUSettlement" &&
                            p.row.iou_status === "MoneyIssued" &&
                            p.row.returned_amount > 0
                        ) {

                            return (

                                <div className="flex items-center justify-center w-full h-full">

                                    <button
                                        onClick={() => handleConfirmReceived(p.row._id)}
                                        className="bg-indigo-600 text-white px-3 py-1 rounded-full text-xs"
                                    >
                                        Confirm Received
                                    </button>

                                </div>
                            );
                        }

                                // ==========================================
                                // IOU SETTLEMENT - EXTRA SPENT
                                // ADMIN ISSUE MONEY
                                // ==========================================

                        if (
                            role === "Accountant" &&
                            p.row.transaction_type === "IOUSettlement" &&
                            p.row.iou_status === "SettlementApproved" &&
                            p.row.returned_amount < 0
                        ) {

                            return (

                                <div className="flex items-center justify-center w-full h-full">

                                    <button
                                        onClick={() => handleIssueMoney(p.row._id)}
                                        className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs"
                                    >
                                        Money Issued
                                    </button>

                                </div>
                            );
                        }

                            // ==========================================
                            // SETTLED
                            // ==========================================

                        if (
                            p.row.iou_status === "Settled"
                        ) {

                            return (
                                <div className="flex items-center justify-center w-full h-full">
                                    <span className="text-green-600 font-semibold ">
                                     Done
                                    </span>
                                </div>
                            );
                        }

                        return null;
                    }
                }] : [])
    ];

    const remaining = selectedRow
        ? selectedRow.amount - (selectedRow.reimbursed_amount || 0)
        : 0;

    return (
        <div className="p-6">

            <div className="flex justify-between items-center mb-6">

                <h1 className="text-3xl font-bold text-green-700">
                    Reimbursements
                </h1>


                {!(role === "Company Admin" || role === "Accountant") && (
                    <button
                        onClick={() => setShowModal(true)}
                        className="bg-green-700 text-white px-4 py-2 rounded-lg flex items-center hover:bg-green-800"
                    >
                        <FaPlus className="mr-2" />
                        Add Reimbursement
                    </button>
                )}
            </div>




            <div className="bg-white p-4 rounded shadow">
                <div className="flex flex-wrap gap-2 mb-4">

                    <input
                        placeholder="Search"
                        className="border p-2 flex-1"
                        onChange={e => updateFilter({ search: e.target.value })}
                    />

                    <select
                        onChange={e => updateFilter({ category: e.target.value })}
                        className="border p-2"
                    >
                        <option value="">All Categories</option>
                        {categories.map(c => (
                            <option key={c._id} value={c._id}>
                                {c.category_name}
                            </option>
                        ))}
                    </select>

                    <select
                        className="border p-2"
                        onChange={e => updateFilter({ location: e.target.value })}>
                        <option value="">All Locations</option>
                        {locations.map(l => (
                            <option key={l._id} value={l._id}>
                                {l.location_name}
                            </option>
                        ))}
                    </select>

                    <input
                        type="date"
                        className="border p-2"
                        onChange={e => updateFilter({ from: e.target.value })}
                    />

                    <input
                        type="date"
                        className="border p-2"
                        onChange={e => updateFilter({ to: e.target.value })}
                    />

                </div>
                <Grid
                    rows={data}
                    columns={columns}
                    getRowId={(r) => r._id}
                    rowCount={totalRows}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    autoRowHeight={true}
                />
            </div>

            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        fontWeight: "bold",
                        color: "#15803d"
                    }}
                >
                    Approve Reimbursement
                </DialogTitle>

                <DialogContent>

                    <div className="mt-3 flex flex-col gap-3">

                        <Typography variant="body2">
                            Remaining Balance :
                            <span className="font-semibold text-green-700 ml-2">
                                 Rs {remaining.toLocaleString()}
                            </span>
                        </Typography>

                        <TextField
                            label="Approve Amount"
                            type="number"
                            fullWidth
                            value={approveAmount}
                            InputProps={{
                                readOnly: true,
                            }}
                        />

                    </div>

                </DialogContent>

                <DialogActions>

                    <Button
                        onClick={() => setOpenDialog(false)}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleApprove}
                        sx={{
                            backgroundColor: "#16a34a"
                        }}
                    >
                        Approve
                    </Button>

                </DialogActions>

            </Dialog>

            <Dialog
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                maxWidth="md"
                fullWidth
            >

                <DialogTitle
                    sx={{
                        fontWeight: "bold",
                        color: "#15803d",
                        textAlign: "center"
                    }}
                >
                    {
                        selectedRow?.transaction_type === "IOUSettlement"
                            ? "IOU Settlement Details"
                            : "Claim Details"
                    }

                </DialogTitle>

                <DialogContent>

                    <div className="flex flex-col gap-4 mt-2">

                        {
                            (
                                selectedRow?.requested_for_user || selectedRow?.created_by
                            ) && (
                                <div
                                    className="
                                        bg-blue-50
                                        border
                                        border-blue-200
                                        rounded-2xl
                                        p-4
                                        flex
                                        items-center
                                        justify-between
                                    "
                                >

                                    <div className="flex flex-col">

                                        <span className="text-xs text-gray-500">
                                            {
                                                selectedRow?.requested_for_user
                                                    ? "Requested User"
                                                    : "Created By"
                                            }
                                        </span>

                                        <span className="font-semibold text-blue-700">
                                            {
                                                selectedRow?.requested_for_user?.username ||
                                                selectedRow?.created_by?.username
                                            }
                                        </span>

                                    </div>

                                    <span
                                        className="
                                            bg-blue-100
                                            text-blue-700
                                            px-3
                                            py-1
                                            rounded-full
                                            text-xs
                                            font-medium
                                        "
                                    >
                                            {
                                                selectedRow?.requested_for_user?.role ||
                                                (
                                                    selectedRow?.requested_for_user
                                                        ? ""
                                                        : "Self Request"
                                                )
                                            }
                                        </span>

                                </div>
                            )
                        }

                        {/* ===================================== */}
                        {/* IOU SUMMARY */}
                        {/* ===================================== */}

                        {
                            selectedRow?.transaction_type === "IOUSettlement" &&
                            (() => {

                                const settlement =
                                    selectedRow.returned_amount || 0;

                                const totalExpenses =
                                    selectedRow.related_transactions?.reduce(
                                        (sum, item) =>
                                            sum +
                                            (
                                                item.transaction_id?.amount || 0
                                            ),
                                        0
                                    ) || 0;

                                const iouAmount =
                                    totalExpenses +
                                    (
                                        settlement > 0
                                            ? settlement
                                            : -Math.abs(settlement)
                                    );

                                return (

                                    <div
                                        className="
                                            bg-gradient-to-r
                                            from-yellow-50
                                            to-orange-50
                                                border
                                            border-yellow-200
                                            rounded-2xl
                                            p-5
                                            shadow-sm
                                                    "
                                    >

                                        {/* TOP */}

                                        <div className="flex items-center justify-between mb-4">

                                            <div>

                                                <h2 className="text-lg font-bold text-gray-800">
                                                    IOU Settlement Summary
                                                </h2>

                                                <p className="text-xs text-gray-500 mt-1">
                                                    Complete settlement breakdown
                                                </p>

                                            </div>

                                            <span
                                                className={`
                                                            px-3
                                                            py-1
                                                            rounded-full
                                                            text-xs
                                                            font-semibold
                                                            ${
                                                                settlement > 0
                                                                         ? "bg-green-100 text-green-700"
                                                                            : settlement < 0
                                                                            ? "bg-red-100 text-red-700"
                                                                            : "bg-blue-100 text-blue-700"
                                                             }
                                                                `}
                                            >
                                                                    {
                                                                    settlement > 0
                                                    ? "Remaining Balance"
                                                     : settlement < 0
                                                        ? "Extra Expenses"
                                                        : "Exact Settlement"
                                                    }
                                            </span>

                                        </div>

                                        {/* BODY */}

                                        <div className="grid grid-cols-3 gap-4">

                                            <div className="bg-white rounded-xl p-4 border">

                                                <p className="text-xs text-gray-500 mb-1">
                                                    IOU Amount
                                                </p>

                                                <h3 className="text-xl font-bold text-gray-800">
                                                    Rs {Math.abs(iouAmount).toLocaleString()}
                                                </h3>

                                            </div>

                                            <div className="bg-white rounded-xl p-4 border">

                                                <p className="text-xs text-gray-500 mb-1">
                                                    Total Expenses
                                                </p>

                                                <h3 className="text-xl font-bold text-red-600">
                                                    Rs {totalExpenses.toLocaleString()}
                                                </h3>

                                            </div>

                                            <div className="bg-white rounded-xl p-4 border">

                                                <p className="text-xs text-gray-500 mb-1">
                                                    Settlement
                                                </p>

                                                <h3
                                                    className={`
                                                                text-xl
                                                                font-bold
                                                                ${
                                                        settlement > 0
                                                            ? "text-green-600"
                                                            : settlement < 0
                                                                ? "text-red-600"
                                                                : "text-blue-600"
                                                                 }
                                                    `}
                                                >
                                                    {
                                                        settlement > 0
                                                            ? `+ Rs ${settlement.toLocaleString()}`
                                                            : settlement < 0
                                                                ? `- Rs ${Math.abs(settlement).toLocaleString()}`
                                                                : "Rs 0"
                                                    }
                                                </h3>

                                            </div>

                                        </div>

                                    </div>

                                );

                            })()
                        }

                        {/* ===================================== */}
                        {/* EXPENSE / CLAIM ITEMS */}
                        {/* ===================================== */}

                        {
                            detailsData.map((item, i) => {

                                const tx = item.transaction_id;

                                const total =
                                    tx?.amount || 0;

                                const paid =
                                    item.reimbursed_amount || 0;

                                const attachments =
                                    tx?.attachments || [];

                                let status =
                                    "Pending";

                                let color =
                                    "bg-gray-100 text-gray-600";

                                if (paid === total) {

                                    status = "Fully Claimed";

                                    color =
                                        "bg-green-100 text-green-700";

                                } else if (paid > 0) {

                                    status =
                                        "Partially Claimed";

                                    color =
                                        "bg-yellow-100 text-yellow-700";
                                }

                                return (

                                    <div
                                        key={i}
                                        className="
                                            border
                                            rounded-2xl
                                            p-5
                                            bg-white
                                            shadow-md
                                            hover:shadow-lg
                                            transition-all
                                        "
                                    >

                                        {/* TOP */}

                                        <div className="flex justify-between items-start">

                                            <div className="flex flex-col">

                                    <span className="font-semibold text-gray-800">
                                        {tx?.transaction_code}
                                    </span>

                                                <span className="text-xs text-gray-500 mt-1">
                                        {
                                            tx?.description ||
                                            "No description"
                                        }
                                    </span>

                                            </div>

                                            {
                                                selectedRow?.transaction_type ===
                                                "Reimbursement" ? (

                                                    <span
                                                        className={`
                                                            text-xs
                                                            px-2
                                                            py-1
                                                            rounded-full
                                                            ${color}
                                                        `}
                                                    >
                                                        {status}
                                                    </span>

                                                ) : (

                                                    <span
                                                        className="
                                                            text-xs
                                                            px-2
                                                            py-1
                                                            rounded-full
                                                            bg-purple-100
                                                            text-purple-700
                                                             "
                                                    >
                                                        IOU Expense
                                                    </span>

                                                )
                                            }

                                        </div>

                                        {/* AMOUNT */}

                                        <div className="mt-3 text-sm flex flex-col gap-1">

                                            <div className="flex justify-between">

                                    <span className="text-gray-500">
                                        Expense Amount
                                    </span>

                                                <span className="font-medium">
                                        Rs {total.toLocaleString()}
                                    </span>

                                            </div>

                                            {
                                                selectedRow?.transaction_type ===
                                                "Reimbursement" && (

                                                    <div className="flex justify-between">

                                            <span className="text-gray-500">
                                                Reimbursed
                                            </span>

                                                        <span className="font-semibold text-blue-600">
                                                Rs {paid.toLocaleString()}
                                            </span>

                                                    </div>
                                                )
                                            }

                                        </div>

                                        {/* ATTACHMENTS */}

                                        {
                                            attachments.length > 0 && (

                                                <div className="mt-4">

                                                    <p className="text-xs font-semibold text-gray-600 mb-2">
                                                        Attachments
                                                    </p>

                                                    <div className="flex flex-wrap gap-2">

                                                        {
                                                            attachments.map(
                                                                (
                                                                    file,
                                                                    index
                                                                ) => {

                                                                    const fileUrl =
                                                                        `${IMAGE_BASE_URL}/${file}`;

                                                                    return (

                                                                        <button
                                                                            key={index}
                                                                            onClick={() => {

                                                                                setPreviewFile(fileUrl);

                                                                                setPreviewOpen(true);
                                                                            }}
                                                                            className="
                                                                    text-xs
                                                                    bg-blue-50
                                                                    hover:bg-blue-100
                                                                    text-blue-700
                                                                    px-3
                                                                    py-1
                                                                    rounded-full
                                                                "
                                                                        >
                                                                            {
                                                                            file.toLowerCase().includes(".pdf")
                                                                                ? `📄 PDF ${index + 1}`
                                                                                : `🖼 Image ${index + 1}`
                                                                            }
                                                                        </button>

                                                                    );
                                                                }
                                                            )
                                                        }

                                                    </div>

                                                </div>

                                            )
                                        }
                                        {
                                            attachments.length === 0 && (

                                                <div className="mt-4">

                                                    <span
                                                        className="
                                                             text-xs
                                                                text-gray-400
                                                                 italic
                                                                         "
                                                    >
                                                            No attachments
                                                    </span>

                                                </div>
                                            )
                                        }

                                    </div>

                                );
                            })
                        }

                    </div>

                </DialogContent>

                <DialogActions>

                    <Button
                        onClick={() => setDetailsOpen(false)}
                        variant="contained"
                        sx={{
                            backgroundColor: "#16a34a"
                        }}
                    >
                        Close
                    </Button>

                </DialogActions>

            </Dialog>

            <Dialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        fontWeight: "bold",
                        color: "#15803d"
                    }}
                >
                    Attachment Preview
                </DialogTitle>

                <DialogContent>

                    {
                        previewFile?.toLowerCase().includes(".pdf") ? (

                            <iframe
                                src={previewFile}
                                title="Attachment"
                                className="w-full h-[600px]"
                            />

                        ) : (

                            <img
                                src={previewFile}
                                alt="Attachment"
                                className="
                                    w-full
                                    h-auto
                                    max-h-[75vh]
                                    object-contain
                                    rounded-lg
                                            "
                            />

                        )
                    }

                </DialogContent>

                <DialogActions>

                    <Button
                        onClick={() => setPreviewOpen(false)}
                        variant="contained"
                        sx={{
                            backgroundColor: "#16a34a"
                        }}
                    >
                        Close
                    </Button>

                </DialogActions>

            </Dialog>

            <Dialog
                open={cashbookDialog}
                onClose={() =>
                    setCashbookDialog(false)
                }
                maxWidth="sm"
                fullWidth
            >

                <DialogTitle>
                    Link Cashbook
                </DialogTitle>

                <DialogContent>

                    <select
                        className="
                w-full
                border
                p-3
                mt-3
                rounded
            "
                        value={selectedCashbook}
                        onChange={(e) =>
                            setSelectedCashbook(
                                e.target.value
                            )
                        }
                    >

                        <option value="">
                            Select Cashbook
                        </option>

                        {
                            cashbooks.map(book => (

                                <option
                                    key={book._id}
                                    value={book._id}
                                >
                                    {book.cash_book_code} - {book.category?.category_name || "No Category"}
                                </option>
                            ))
                        }

                    </select>

                </DialogContent>

                <DialogActions>

                    <Button
                        onClick={() =>
                            setCashbookDialog(false)
                        }
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleLinkCashbook}
                    >
                        Link
                    </Button>

                </DialogActions>

            </Dialog>

            <ReimbursementModal
                open={showModal}
                onClose={() => setShowModal(false)}
                onSuccess={() => {
                    fetchReimbursements();
                    setShowModal(false);
                    window.dispatchEvent(
                        new Event("pettycash-updated")
                    );
                }}
            />

        </div>
    );
}

export default ReimbursementPage;